/**
 * RejectionNotificationModal — confirms a rejection after the reject POST
 * lands (single or bulk). Carbon modal (components/carbon/Modal): a pinned
 * header, the body, and one primary "Understood" action.
 *
 * Only reachable after a write, so the harness verifies it through the
 * gallery's modal state plus an SSR render test with `isOpen` true.
 */

import { Modal } from '@/components/carbon/Modal'
import { RowDetailSection } from '@/components/carbon/RowDetail'

interface RejectionNotificationModalProps {
  isOpen: boolean
  onClose: () => void
  rejectedCount: number
  isBulkAction?: boolean
}

export default function RejectionNotificationModal({
  isOpen,
  onClose,
  rejectedCount,
  isBulkAction = false,
}: RejectionNotificationModalProps) {
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      title="Drift rejection recorded"
      primaryAction={{ label: 'Understood', onClick: onClose }}
    >
      <div className="flex flex-col gap-6">
        <p className="m-0 text-body text-text-secondary">
          This rejection is <b className="font-semibold text-text-primary">documented with your comments</b> and recorded
          in the audit log. If your organization has a ticketing system configured, a notification goes out to
          it.
        </p>

        {isBulkAction && rejectedCount > 1 ? (
          <p className="m-0 text-body text-text-secondary">
            <b className="font-semibold text-text-primary">{rejectedCount} drifts</b> are rejected and documented.
          </p>
        ) : null}

        <RowDetailSection title="Manual remediation">
          <p className="m-0">
            To revert the change in Microsoft 365, open the drift&apos;s details. They show the{' '}
            <b>current value</b> (what changed) and the <b>expected value</b> (your baseline). Restore the setting
            to its expected value, and the next scan shows the drift as <b>Remediated</b>.
          </p>
        </RowDetailSection>

        <p className="m-0 text-helper text-text-helper">
          Your rejection reason and the user who rejected this drift stay on record for compliance and audit.
        </p>
      </div>
    </Modal>
  )
}
